import GooglePayButton from "@google-pay/button-react";
import axios from "axios";
import {
  BAD_REQUEST_PAGE,
  SERVER_ERROR_PAGE,
  SUCCESS_PAGE,
} from "../../constants/path.c";
// import { Buffer } from "buffer";
import { getLanguagePrefix } from "../hooks/getLanguagePrefix";
import { performRCP, encryptText } from "../../utils/helper";
import { cardinal3ds } from "../hooks/cardinal3ds";
import JwtFrame from "../integration/JwtFrame";
import { BASE_API_URL } from "../../constants/api-path.c";
import "./style.css";
import { applePayController } from "../apple-pay-button/components/apple-pay";
import {  useGoogleAnalyticsClickEvent, categoryGA } from '../hooks/google-analytics';
import { useEffect, useRef, useState } from "react";
import { apmKeyApplePayKey, apmKeys } from "../../constants/common.c";

export const DasGooglePayButton = ({
  apiData,
  slug,
  navigate,
  setDasLoader,
}: any) => {
  let [isFullWidthButton,setIsFullWidthButton] = useState(true);
  // 3DS2 Device Data Collection state. Worldpay requires a dfReferenceId for every
  // Google Pay transaction; since we never see the PAN/BIN, we run Cardinal DDC with a
  // BLANK bin up-front so the dfReferenceId (SessionId) is ready by authorisation time.
  let [ddcJwt, setDdcJwt] = useState<any>(null);
  // DDC SessionId (= dfReferenceId) kept in a ref so handleLoadPaymentData always reads the
  // latest value. The Google Pay button can hold a stale closure where the state copy is
  // still "" (DDC resolves after the button captured the handler), which silently drops 3DS.
  const sessionIDRef = useRef<any>("");
  useEffect(() => {
    // Run DDC only when the product is 3DS-enabled. For non-3DS Google Pay the dfReferenceId
    // is unused (gateway gates on Product.has3DS), so skip the /threeds/jwt/create call and
    // the Cardinal Collect iframe entirely.
    if (apiData?.has3DS) {
      cardinal3ds(slug, (id: any) => { sessionIDRef.current = id; }, setDdcJwt, navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, apiData?.has3DS]);
  useEffect(()=>{
    const componentKey = "hasGooglePay";
    let applicableKey = apmKeys.filter(i=> i!=componentKey && apiData[i] );
    if (applicableKey.length > 0 ) {
        if (applicableKey.length == 1 && applicableKey.includes(apmKeyApplePayKey) && applePayController.isAppleSessionAvailable()) {
          setIsFullWidthButton(false)
        }else{
          setIsFullWidthButton(true)
        }
    };
  },[apiData])
  
  const pushClickEventAnalytics = useGoogleAnalyticsClickEvent();  // For google analytics
  function onPaymentAuthorized(paymentData: any) {
      localStorage.setItem("slug", slug);

    const { token } = paymentData?.paymentMethodData?.tokenizationData;
    if (token) {
      return Promise.resolve({ transactionState: "SUCCESS" });
    } else {
      return Promise.resolve({
        transactionState: "ERROR",
        error: {
          intent: "PAYMENT_AUTHORIZATION",
          message: "Insufficient funds",
          reason: "PAYMENT_DATA_INVALID",
        },
      });
    }
  }
  function handleLoadPaymentData(paymentData: any) {
    pushClickEventAnalytics(categoryGA.googlePay,` ${slug} : payment submitted`); // For google analytics

    setDasLoader(true);

    return new Promise(function (resolve, reject) {

      const { token } = paymentData?.paymentMethodData?.tokenizationData;
      const { info } = paymentData?.paymentMethodData;

      const BACKEND_URL_PAY = `${BASE_API_URL}/wallet/capture/payment`;
      const payload = {
        URLKey: slug,
        amount: Number(apiData.amount),
        payment_method: {
          network:info?.cardNetwork,
          type: "googlepay",
          googlePayToken: JSON.stringify(token),
        },
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // 3DS2 dfReferenceId from the Cardinal DDC (run with a blank bin). Mandatory for
        // 3DS Flex; encrypted exactly like the card flow. Backend decrypts + forwards it.
        ...(sessionIDRef.current && { dfReferenceId: encryptText(sessionIDRef.current) }),
        // 3DS2 browser data (same shape as the card flow). Used when the product is
        // 3DS-enabled so Worldpay can authenticate a PAN_ONLY Google Pay token.
        browser_info: {
          language: navigator?.language || "",
          screenHeight: window.screen?.width,
          screenWidth: window.screen?.height,
          userAgent: navigator?.userAgent || "",
          javaEnabled: navigator?.javaEnabled ? navigator.javaEnabled() : false,
          javascriptEnabled: true,
          screenColorDepth: window.screen?.colorDepth?.toString() || "",
        },
      };
      // Make an HTTP POST request to your server to process the payment
      axios
        .post(BACKEND_URL_PAY, payload, {
          headers: {
            "x-secret-key": process.env.REACT_APP_PUBLIC_SECRET_KEY,
            "x-api-key": process.env.REACT_APP_PUBLIC_API_KEY,
          },
        })
        .then((response: any) => response.data)
        .then((result: any) => {
          // 3DS2 challenge for Google Pay (PAN_ONLY): hand the challenge to the useEffect
          // above so performRCP runs OUTSIDE this Google Pay callback (see note there). The
          // form submits to _parent; the gateway 3DS webhook then completes authentication
          // and redirects to the success/decline page.
          if (result?.jwt) {
            // Defer performRCP OUT of this Google Pay onLoadPaymentData callback. Called
            // synchronously here the browser downloads the response instead of navigating;
            // setTimeout runs it on a fresh macrotask (no React state/re-render needed).
            const jwt = result.jwt;
            const mid = result.MID;
            performRCP(jwt, mid);
            //setTimeout(() => performRCP(jwt, mid), 0);
            //return;
          }
          // Handle the server's response here
          else if (result.success) {
            let redirectURL;
            // const objJsonStr = JSON.stringify(result);
            // const objJsonB64 = Buffer.from(objJsonStr).toString("base64");
            let newResult = result?.Ref;
            if (result.hasOwnProperty("success") && result.success === true) {
              redirectURL = `/${SUCCESS_PAGE}/${newResult}`;
            } else {
              redirectURL = `/${BAD_REQUEST_PAGE}`;
            }
            navigate(`${getLanguagePrefix()}${redirectURL}`);
            // resolve({});
          } else {
            // Handle the payment error case
            reject({ message: result.message });
          }
        })
        .catch((error) => {
          const result = error.response.data;
          let redirectURL;
          // const objJsonStr = JSON.stringify(result);
          // const objJsonB64 = Buffer.from(objJsonStr).toString("base64");
          if (result.hasOwnProperty("success") && result.success === false) {
            redirectURL = `/${SERVER_ERROR_PAGE}/${result?.Ref}`;
          } else {
            redirectURL = `/${BAD_REQUEST_PAGE}`;
          }
          navigate(`${getLanguagePrefix()}${redirectURL}`);
          // reject({ message: "Payment processing failed" });
        });
    });
  }
  return (
    <div
      className={
        isFullWidthButton ? "google-pay-button full-width-button" : "google-pay-button"
      }
    >
      {/* Hidden Cardinal DDC iframe. Blank bin because Google Pay exposes no PAN/BIN;
          Cardinal posts back the SessionId (-> dfReferenceId) captured in cardinal3ds. */}
      {ddcJwt && <JwtFrame jwt={ddcJwt} bin={""} />}
      <GooglePayButton
        environment={
          (process.env.REACT_APP_PUBLIC_GOOGLE_PAY_ENVIRONMENT || "TEST") as
            | "TEST"
            | "PRODUCTION"
        }
        paymentRequest={{
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: "CARD",
              parameters: {
                allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                allowedCardNetworks: ["MASTERCARD", "VISA", "AMEX"],
              },
              tokenizationSpecification: {
                type: "PAYMENT_GATEWAY",
                parameters: {
                  gateway:
                    process.env.REACT_APP_PUBLIC_GOOGLE_PAY_GATEWAY ||
                    "worldpay",
                  gatewayMerchantId: apiData?.gGatewayMchId || process.env.REACT_APP_PUBLIC_GOOGLE_PAY_GETEWAY_MERCHANT_ID || "",
                },
              },
            },
          ],
          merchantInfo: {
            merchantId: apiData?.gMchId || process.env.REACT_APP_PUBLIC_GOOGLE_PAY_MERCHANT_ID || "",
            merchantName: apiData?.company,
          },
          transactionInfo: {
            totalPriceStatus: "FINAL",
            totalPriceLabel: "Total",
            totalPrice: apiData?.amount.toString() || "0",
            currencyCode: apiData?.currency,
            countryCode: apiData?.country?.value,
            displayItems: [
              {
                label: "Subtotal",
                type: "SUBTOTAL",
                price: apiData?.amount.toString(),
              },
              // {
              //   label: 'Tax',
              //   type: 'TAX',
              //   price: '1.00',
              // },
            ],
          },
          // optional to enable shipping address
          shippingAddressRequired: true,
          callbackIntents: ["PAYMENT_AUTHORIZATION"],
        }}
        onLoadPaymentData={handleLoadPaymentData}
        onPaymentAuthorized={onPaymentAuthorized as any}
        buttonType="pay"
        style={{ width: 480, height: 30 }}
        buttonSizeMode="fill"
      />
    </div>
  );
};
